import { Check, Copy, Mail, Send, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { TranslationKey } from "./i18n";

type Translator = (key: TranslationKey, values?: Record<string, string | number>) => string;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

function trackShare(method: string, itemId: string) {
  window.gtag?.("event", "share", { method, content_type: "page", item_id: itemId });
  window.clarity?.("event", `share_${method}`);
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

export function QuickShareButton({
  title,
  text,
  url,
  itemId,
  label,
}: {
  title: string;
  text: string;
  url: string;
  itemId: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        trackShare("native", itemId);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
      return;
    }
    await copyText(url);
    setCopied(true);
    trackShare("copy", itemId);
  };

  return (
    <button className={`icon-button ${copied ? "is-success" : ""}`} type="button" onClick={() => void share()} aria-label={label} title={label}>
      {copied ? <Check size={18} /> : <Share2 size={18} />}
    </button>
  );
}

export function SharePanel({
  title,
  text,
  url,
  itemId,
  t,
}: {
  title: string;
  text: string;
  url: string;
  itemId: string;
  t: Translator;
}) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    await copyText(url);
    setCopied(true);
    trackShare("copy", itemId);
  };

  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title, text, url });
      trackShare("native", itemId);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) await copy();
    }
  };

  const socialLinks = [
    ["X", `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, "x"],
    ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, "linkedin"],
    ["Reddit", `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`, "reddit"],
  ];

  return (
    <aside className="share-panel" aria-label={t("share.title")}>
      <div>
        <span>{t("share.kicker")}</span>
        <strong>{t("share.title")}</strong>
        <p>{t("share.description")}</p>
      </div>
      <div className="share-actions">
        <button type="button" onClick={() => void copy()}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? t("share.copied") : t("share.copy")}
        </button>
        <button type="button" onClick={() => void nativeShare()}>
          <Send size={15} /> {t("share.more")}
        </button>
        {socialLinks.map(([label, href, method]) => (
          <a key={method} href={href} target="_blank" rel="noreferrer" onClick={() => trackShare(method, itemId)}>
            <Share2 size={14} /> {label}
          </a>
        ))}
        <a href={`mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`${text}\n\n${url}`)}`} onClick={() => trackShare("email", itemId)}>
          <Mail size={14} /> {t("share.email")}
        </a>
      </div>
    </aside>
  );
}
